import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { BookGrid } from '@/components/books/BookGrid';
import { BookDetailModal } from '@/components/books/BookDetailModal';
import { FilterPill } from '@/components/filters/FilterPill';
import { FilterRail, EMPTY_FILTERS, type CatalogFilters } from '@/components/filters/FilterRail';
import { SectionTitle } from '@/components/ui/SectionTitle';
import { booksService } from '@/services/books.service';
import { useReadersStore } from '@/store/readers.store';
import { useGuestStore } from '@/store/guest.store';
import { useIsGuest } from '@/hooks/useIsGuest';
import { ApiError } from '@/services/http';
import { LABEL_TYPES, LABEL_TYPE_SINGULAR, type LabelType } from '@/constants/label-types';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * Catalog — `/catalog`. The full filterable browse view per
 * docs/CLIENT_PLAN.md. URL is the single source of truth: the component
 * reads query params on mount, pushes them on every filter change, and
 * re-fetches whenever they move. This is what lets deep links like
 * `?genre=Fantasy&genre=Mystery` work from the Library's editorial card.
 *
 * Layout:
 *   - Kid mode AppShell so the page feels continuous with Library (where
 *     users arrive via "Explore even more →"). The filter rail itself
 *     adopts a slate/white style regardless of mode.
 *   - Grid-on-left + rail-on-right on ≥lg; stacked on smaller viewports.
 *   - Row of `FilterPill` chips above the grid reflecting the active
 *     values; clicking a pill's body re-opens the rail at that section,
 *     clicking × removes the value.
 *
 * Pagination uses the `page` query param (1-based) with a fixed `limit`
 * of 20. We could read `limit` from the URL too but nothing currently
 * sets it, so we keep it simple.
 */
const PAGE_SIZE = 20;

export function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // --- URL → filters + page. Derived every render, not state, so the URL
  // stays the source of truth when the browser back button fires. ---
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const [books, setBooks] = useState<BookView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState<BookView | null>(null);
  const [openSection, setOpenSection] = useState<LabelType | null>(null);

  // --- Pre-filter the catalog to the selected reader ------------------------
  // Scope the reading-level filter to the selected reader's levels so the
  // catalog opens on level-appropriate books. On the FIRST load we honor any
  // filters already in the URL (deep links from the Library "Explore even
  // more →" card, or browser back-nav) and leave them alone; a later reader
  // *switch* re-scopes the reading level while preserving other chosen filters.
  //
  // Only reading levels are auto-applied: the reader API returns `interests`
  // without their label type (genre/topic/interest_level are merged into one
  // bucket), so they can't be reliably slotted into the matching filter
  // sections client-side.
  const selectedReader = useReadersStore((s) => s.selectedReader());
  const seededReaderRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedReader) return; // wait for the reader to hydrate
    const readerId = selectedReader.id;
    if (seededReaderRef.current === readerId) return; // already handled this reader
    const firstRun = seededReaderRef.current === undefined;
    seededReaderRef.current = readerId;

    const hasUrlFilters = LABEL_TYPES.some((type) => filters[type].length > 0);
    if (firstRun && hasUrlFilters) return; // honor deep-linked filters on arrival

    const levels = selectedReader.readingLevels.map((l) => l.labelValue);
    writeParams(
      { filters: { ...filters, reading_level: levels }, page: 1 },
      setSearchParams,
      { replace: true },
    );
  }, [selectedReader, filters, setSearchParams]);

  // --- Guest: seed the reading-level filter from the guest's picks ----------
  // Mirror the reader seeding above for unauthenticated visitors. Runs once;
  // honors deep-linked URL filters, and skips when the guest chose "browse
  // all levels" (empty selection).
  const isGuest = useIsGuest();
  const guestLevels = useGuestStore((s) => s.readingLevels);
  const guestHasChosen = useGuestStore((s) => s.hasChosenLevels);
  const guestSeededRef = useRef(false);
  useEffect(() => {
    if (!isGuest || !guestHasChosen) return;
    if (guestSeededRef.current) return;
    guestSeededRef.current = true;
    if (guestLevels.length === 0) return; // "browse all" → no constraint
    const hasUrlFilters = LABEL_TYPES.some((type) => filters[type].length > 0);
    if (hasUrlFilters) return; // honor deep-linked filters on arrival
    writeParams(
      { filters: { ...filters, reading_level: guestLevels.map((l) => l.alias) }, page: 1 },
      setSearchParams,
      { replace: true },
    );
  }, [isGuest, guestHasChosen, guestLevels, filters, setSearchParams]);

  // --- Primary fetch: re-runs whenever the URL moves. ---
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const offset = (page - 1) * PAGE_SIZE;
    (async () => {
      try {
        const result = await booksService.list({
          ...filters,
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelled) return;
        setBooks(result.books);
        setTotalCount(result.totalCount);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load the catalog.');
        setBooks([]);
        setTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters, page]);

  // --- Keep ?page valid when the filter set shrinks the result count. ---
  useEffect(() => {
    if (loading) return;
    if (totalCount === 0 && page > 1) {
      writeParams({ filters, page: 1 }, setSearchParams, { replace: true });
      return;
    }
    const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (page > lastPage) {
      writeParams({ filters, page: lastPage }, setSearchParams, { replace: true });
    }
  }, [loading, totalCount, page, filters, setSearchParams]);

  const applyFilters = useCallback(
    (next: CatalogFilters) => {
      // Filter changes always reset to page 1; staying on page 7 after
      // narrowing the set is almost never what the user meant.
      writeParams({ filters: next, page: 1 }, setSearchParams);
    },
    [setSearchParams],
  );

  const removePill = useCallback(
    (type: LabelType, alias: string) => {
      const next: CatalogFilters = { ...filters, [type]: filters[type].filter((v) => v !== alias) };
      writeParams({ filters: next, page: 1 }, setSearchParams);
    },
    [filters, setSearchParams],
  );

  const clearAll = useCallback(() => {
    writeParams({ filters: EMPTY_FILTERS, page: 1 }, setSearchParams);
  }, [setSearchParams]);

  const activePills = useMemo(() => {
    const out: Array<{ type: LabelType; alias: string }> = [];
    for (const type of LABEL_TYPES) {
      for (const alias of filters[type]) out.push({ type, alias });
    }
    return out;
  }, [filters]);

  const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);

  const goToPage = (next: number) => {
    const clamped = Math.max(1, Math.min(lastPage, next));
    if (clamped === page) return;
    writeParams({ filters, page: clamped }, setSearchParams);
    // Scroll back to the top of the grid so "Next" feels natural rather
    // than leaving the user mid-scroll on the next page's second row.
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AppShell mode="kid">
      <div className="flex flex-col gap-6">
        <SectionTitle kicker="Browse" heading="Catalog" />

        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          {/* --- Results column --- */}
          <div className="min-w-0 flex-1">
            {activePills.length > 0 ? (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {activePills.map((pill) => (
                  <FilterPill
                    key={`${pill.type}:${pill.alias}`}
                    kicker={LABEL_TYPE_SINGULAR[pill.type]}
                    value={pill.alias}
                    onOpen={() => setOpenSection(pill.type)}
                    onRemove={() => removePill(pill.type, pill.alias)}
                  />
                ))}
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[12px] font-semibold text-or-orange-600 hover:text-or-orange-700"
                >
                  Clear all
                </button>
              </div>
            ) : null}

            <div className="mb-3 text-[12px] text-or-slate-600">
              {loading ? (
                <span>Searching the shelves…</span>
              ) : totalCount === 0 ? (
                <span>No books match.</span>
              ) : (
                <span>
                  Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{' '}
                  {totalCount.toLocaleString()} books
                </span>
              )}
            </div>

            <BookGrid
              books={books}
              loading={loading}
              onSelectBook={setSelectedBook}
              emptyState={<EmptyCatalog hasFilters={activePills.length > 0} onClear={clearAll} />}
            />

            {!loading && totalCount > PAGE_SIZE ? (
              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className={cn(
                    'inline-flex h-9 items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3 text-[12px] font-semibold text-or-slate-700 transition',
                    'hover:bg-or-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                  Previous
                </button>
                <div className="text-[12px] text-or-slate-500">
                  Page {page} of {lastPage}
                </div>
                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= lastPage}
                  className={cn(
                    'inline-flex h-9 items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3 text-[12px] font-semibold text-or-slate-700 transition',
                    'hover:bg-or-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
            ) : null}
          </div>

          {/* --- Filter rail --- */}
          {/* `order-first` floats the rail to the top on mobile (collapsed by
              default); `lg:order-none` restores the natural right-column order
              on desktop. */}
          <div className="order-first lg:order-none lg:w-[300px] lg:shrink-0">
            <FilterRail
              value={filters}
              onApply={applyFilters}
              initialOpenSection={openSection}
              className="lg:sticky lg:top-24"
            />
          </div>
        </div>
      </div>

      <BookDetailModal
        book={selectedBook}
        onOpenChange={(open) => {
          if (!open) setSelectedBook(null);
        }}
        onSelectBook={(next) => setSelectedBook(next)}
      />
    </AppShell>
  );
}

function EmptyCatalog({ hasFilters, onClear }: { hasFilters: boolean; onClear: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white/80 px-8 py-12 text-center">
      <p className="font-display text-[18px] font-bold text-or-slate-900">No books match</p>
      <p className="mt-1 text-[13px] text-or-slate-600">
        {hasFilters
          ? 'Try removing a filter or two — some combinations leave the shelf empty.'
          : 'The catalog looks empty right now. Please check back soon.'}
      </p>
      {hasFilters ? (
        <button
          type="button"
          onClick={onClear}
          className="or-gradient-btn mt-5 inline-flex h-10 items-center gap-1.5 rounded-full px-5 text-[13px] font-semibold"
        >
          Clear all filters
        </button>
      ) : null}
    </div>
  );
}

/**
 * Reads a `CatalogFilters` object out of the current `URLSearchParams`.
 * Repeated keys (`?genre=Fantasy&genre=Mystery`) are preserved in order,
 * and unrecognized keys are ignored so arbitrary URL junk doesn't break
 * the shape. `searchParams.getAll` already handles the repeated-key
 * semantics — no manual splitting required.
 */
function readFilters(searchParams: URLSearchParams): CatalogFilters {
  const out: CatalogFilters = {
    genre: [],
    topic: [],
    reading_level: [],
    interest_level: [],
    historical_event: [],
    time_period: [],
    book_series: [],
  };
  for (const type of LABEL_TYPES) {
    const values = searchParams.getAll(type).filter((v) => v.trim() !== '');
    out[type] = values;
  }
  return out;
}

/**
 * Serializes filters + page back into `URLSearchParams`. Empty label-type
 * arrays are omitted so the URL stays tidy, and `page=1` is omitted for
 * the same reason — we only materialize it when the user pages forward.
 *
 * The `setSearchParams` call must run inside the React tree (we pass the
 * setter in) so React Router can notify subscribers; updating
 * `window.history` directly would miss the `useSearchParams` hook.
 */
function writeParams(
  next: { filters: CatalogFilters; page: number },
  setSearchParams: SetURLSearchParams,
  options: { replace?: boolean } = {},
) {
  const params = new URLSearchParams();
  for (const type of LABEL_TYPES) {
    for (const alias of next.filters[type]) params.append(type, alias);
  }
  if (next.page > 1) params.set('page', String(next.page));
  setSearchParams(params, options);
}

/**
 * Exported for tests + the wiring layer. Lets the App router push a
 * catalog route with pre-configured filters without duplicating the
 * serialization rules.
 */
export function buildCatalogPath(partial: Partial<CatalogFilters>): string {
  const params = new URLSearchParams();
  for (const type of LABEL_TYPES) {
    const values = partial[type];
    if (!values) continue;
    for (const alias of values) params.append(type, alias);
  }
  const qs = params.toString();
  return qs ? `/catalog?${qs}` : '/catalog';
}
